// src/pages/Referrals.jsx
import { useEffect, useState } from "react";
import { Gift, Copy, Users, CheckCircle2, Clock, Share2 } from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";

export default function Referrals() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/referral/my-referral");
        if (res.data?.success) setData(res.data);
      } catch {
        toast.error("Failed to load referral info");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const shareWhatsApp = () => {
    if (!data) return;
    const msg = `Join LarryDigitals and get SMS verification, social accounts & more! Sign up with my link: ${data.referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400">
        Failed to load referral info. Please refresh.
      </div>
    );
  }

  return (
    <div className="animate-fadeIn max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Gift className="text-purple-600 dark:text-purple-400" size={24} />
          Refer & Earn
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Earn ₦{data.bonusPerReferral} for every friend who makes their first purchase
        </p>
      </div>

      {/* Hero card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 dark:from-purple-700 dark:to-purple-950 p-6 sm:p-7 mb-6 shadow-lg">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -right-2 -bottom-10 w-28 h-28 rounded-full bg-white/5" />

        <div className="relative z-10">
          <p className="text-xs text-purple-200 uppercase tracking-wide font-medium mb-1">
            Your Referral Code
          </p>
          <p className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-widest font-mono">
            {data.referralCode}
          </p>

          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="flex-1 flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2.5 min-w-0">
              <p className="text-xs text-white/90 truncate flex-1">{data.referralLink}</p>
              <button
                onClick={() => copy(data.referralLink, "Referral link")}
                className="flex-shrink-0 p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition"
              >
                <Copy size={14} className="text-white" />
              </button>
            </div>
            <button
              onClick={shareWhatsApp}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-purple-700 text-sm font-semibold rounded-xl hover:bg-purple-50 transition flex-shrink-0"
            >
              <Share2 size={15} />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
            <Users size={15} />
            <span className="text-xs font-medium">Total Referred</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.stats.totalReferred}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
            <CheckCircle2 size={15} />
            <span className="text-xs font-medium">Completed</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.stats.completedReferred}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
            <Clock size={15} />
            <span className="text-xs font-medium">Pending</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.stats.pendingReferred}
          </p>
        </div>
      </div>

      {/* Total earned */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs text-green-700 dark:text-green-400 font-medium uppercase tracking-wide mb-1">
            Total Earned
          </p>
          <p className="text-2xl font-bold text-green-800 dark:text-green-300">
            ₦{data.stats.totalEarned.toLocaleString()}
          </p>
        </div>
        <Gift size={32} className="text-green-500 dark:text-green-400 opacity-50" />
      </div>

      {/* How it works */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
        <h3 className="font-semibold mb-2 text-sm text-purple-800 dark:text-purple-200">
          How it works
        </h3>
        <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
          <li>• Share your link or code with friends</li>
          <li>• They sign up using your link</li>
          <li>• You earn ₦{data.bonusPerReferral} once they complete their first purchase — numbers, social accounts, gifts, or anything on the site</li>
          <li>• Bonus is added to your wallet automatically, no waiting</li>
        </ul>
      </div>
    </div>
  );
}